import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import DragAndResizeModal from 'components/Modal/DragAndResizeModal';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { ApiErrorDataI } from 'declarations/interfaces/errors';
import { extractErrorExtensionField } from 'helpers/errors/extractErrorExtensionField';

const DARK_GREY_BG = '#2B2B2B';
const DARK_GREY_PANEL = '#1E1E1E';
const LIGHT_TEXT = '#E6E6E6';
const SUBTLE_BORDER = 'rgba(255, 255, 255, 0.12)';

interface ErrorDetailsModalProps {
  errorDetails: ApiErrorDataI;
  errorSource?: string;
  onClose: () => void;
  open: boolean;
}

const ErrorDetailsModal: FC<ErrorDetailsModalProps> = ({
  errorDetails,
  errorSource,
  onClose,
  open,
}): JSX.Element => {
  const { isMobile } = useResponsive();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  const prettyJson = useMemo((): string => JSON.stringify(errorDetails, null, 2), [errorDetails]);

  const traceId = extractErrorExtensionField(errorDetails, 'traceId');
  const developerMessage = extractErrorExtensionField(errorDetails, 'developerMessage');
  const errorType = extractErrorExtensionField(errorDetails, 'errorType');
  const stackTrace = extractErrorExtensionField(errorDetails, 'stackTrace');

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prettyJson);
      setCopyStatus('copied');
      window.setTimeout((): void => setCopyStatus('idle'), 1500);
    } catch {
      setCopyStatus('failed');
      window.setTimeout((): void => setCopyStatus('idle'), 1500);
    }
  };

  const codeBlockSx = {
    backgroundColor: DARK_GREY_PANEL,
    border: `1px solid ${SUBTLE_BORDER}`,
    borderRadius: 1,
    color: LIGHT_TEXT,
    fontFamily: 'Consolas, Menlo, Monaco, "Courier New", monospace',
    fontSize: 12,
    overflowWrap: 'break-word' as const,
    p: 2,
    whiteSpace: 'pre-wrap' as const,
    width: '100%',
    wordBreak: 'break-word' as const,
  };

  return (
    <DragAndResizeModal
      open={open}
      handleClose={onClose}
      modalTitle="Error details"
      size={ModalSizeE.MD}
      popupId={ScrollLockIdE.ErrorDetailsPopup}
      isMobile={isMobile}
      dialogStyles={{
        '& .MuiPaper-root': {
          backgroundColor: `${DARK_GREY_BG} !important`,
          color: LIGHT_TEXT,
        },
        '& #draggable-dialog-title .MuiTypography-root': {
          color: LIGHT_TEXT,
        },
        '& #draggable-dialog-title .MuiIconButton-root': {
          color: LIGHT_TEXT,
        },
      }}
      dialogContentStyles={{
        backgroundColor: DARK_GREY_BG,
        color: LIGHT_TEXT,
      }}
    >
      <Box sx={{ p: 3, color: LIGHT_TEXT }}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            gap={2}
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              {errorSource && (
                <Typography
                  sx={{ color: LIGHT_TEXT, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Source: {errorSource}
                </Typography>
              )}
              {traceId && (
                <Typography
                  sx={{ color: LIGHT_TEXT, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Trace ID: {traceId}
                </Typography>
              )}
              {Number.isFinite(errorDetails.status) && errorDetails.status > 0 && (
                <Typography
                  sx={{ color: LIGHT_TEXT, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Status: {errorDetails.status}
                </Typography>
              )}
              {errorDetails.title && (
                <Typography
                  sx={{ color: LIGHT_TEXT, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Title: {errorDetails.title}
                </Typography>
              )}
              {errorDetails.detail && (
                <Typography
                  sx={{
                    color: LIGHT_TEXT,
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                  variant="body2"
                >
                  Detail: {errorDetails.detail}
                </Typography>
              )}
              {errorType && (
                <Typography
                  sx={{ color: LIGHT_TEXT, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Error type: {errorType}
                </Typography>
              )}
            </Box>
            <Button
              onClick={handleCopy}
              size="small"
              variant="outlined"
              sx={{
                color: LIGHT_TEXT,
                borderColor: SUBTLE_BORDER,
                '&:hover': {
                  borderColor: LIGHT_TEXT,
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                },
              }}
            >
              {((): string => {
                if (copyStatus === 'copied') return 'Copied';
                if (copyStatus === 'failed') return 'Copy failed';
                return 'Copy JSON';
              })()}
            </Button>
          </Box>

          {errorDetails.errors && Object.keys(errorDetails.errors).length > 0 && (
            <>
              <Divider sx={{ borderColor: SUBTLE_BORDER }} />
              <Box width="100%">
                <Typography gutterBottom variant="subtitle2" sx={{ color: LIGHT_TEXT }}>
                  Validation errors
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {Object.entries(errorDetails.errors).map(([field, messages]) => (
                    <li key={field}>
                      <Typography
                        sx={{
                          color: LIGHT_TEXT,
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                        variant="body2"
                      >
                        <strong>{field}:</strong> {messages.join(', ')}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {(developerMessage || stackTrace) && (
            <>
              <Divider sx={{ borderColor: SUBTLE_BORDER }} />
              {developerMessage && (
                <Box width="100%">
                  <Typography gutterBottom variant="subtitle2" sx={{ color: LIGHT_TEXT }}>
                    Developer message
                  </Typography>
                  <Box sx={codeBlockSx}>
                    <Typography
                      sx={{ color: LIGHT_TEXT, fontFamily: 'inherit', fontSize: 'inherit' }}
                      variant="body2"
                    >
                      {developerMessage}
                    </Typography>
                  </Box>
                </Box>
              )}
              {stackTrace && (
                <Box width="100%">
                  <Typography gutterBottom variant="subtitle2" sx={{ color: LIGHT_TEXT }}>
                    Stack trace
                  </Typography>
                  <Box sx={codeBlockSx}>{stackTrace}</Box>
                </Box>
              )}
            </>
          )}

          <Divider sx={{ borderColor: SUBTLE_BORDER }} />

          <Box width="100%">
            <Typography gutterBottom variant="subtitle2" sx={{ color: LIGHT_TEXT }}>
              Raw payload
            </Typography>
            <Box sx={codeBlockSx}>{prettyJson}</Box>
          </Box>
        </Box>
      </Box>
    </DragAndResizeModal>
  );
};

export default ErrorDetailsModal;
